import { NODE_CREATED, SCREENSHOT_CAPTURED } from 'constants/mixpanel';
import { loadFonts } from 'hooks/useFont';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { fireMPOnCopyWidget, fireMPOnDuplicateWidget } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { RootState, addNewWidget, groupSelectedWidgets, store, unGroupSelectedWidgets } from 'modules/Editor/store';
import { selectInfographId, selectPage, selectWidgets } from 'modules/Editor/store/infographSelector';
import { WidgetId } from 'types/idTypes';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/localStorage';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

import { IgnoreEventElement, IgnoreEventRole } from './Editor.config';
import { COPY_WIDGET_LOCAL_STORAGE_KEY } from './components/Clipboard/Clipboard.config';
import { createCopyWidgets, parseCopyWidgets } from './components/Clipboard/Clipboard.helpers';
import { LocalStorageClipboardWidgets } from './components/Clipboard/Clipboard.types';
import { moveWidgetInLayer } from './store/infographSlice';
import { findFontsInObjectOrArray } from './store/infographSlice.helpers';
import { WidgetDirection } from './store/infographSlice.types';
import { addToPageClipboard, setFontsLoaded } from './store/pageControlSlice';
import { selectActivePage } from './store/pageSelector';
import { addToWidgetClipboard } from './store/widgetControlSlice';
import { selectActiveWidgets, selectWidgetClipboard } from './store/widgetSelector';

/**
 * Checks if the target should be ignored by global event listeners
 * (e.g. for input element the global keyboard shortcut will be ignored)
 *
 * @param target - The target element
 * @param customValidation - A custom validation function for allowing global event listeners to be triggered
 */
export const shouldIgnoreEventTarget = (
  target: HTMLElement,
  customValidation?: (target: HTMLElement) => boolean | undefined,
): boolean => {
  if (!target) return false;
  if (!target.getAttribute) return false;

  if (customValidation) {
    const customVal = customValidation(target);
    if (typeof customVal === 'boolean') return customVal;
  }

  const tagName = target?.tagName as IgnoreEventElement;
  if (tagName && IgnoreEventElement[tagName]) return true;

  const role = target.getAttribute('role') as IgnoreEventRole;
  if (role && IgnoreEventRole[role]) return true;

  return false;
};

/**
 * Copy active widgets, also track the event source.
 *
 * @param from The copy event source.
 */
export const copyActiveWidgets = (from?: 'Keyboard' | 'Context Menu') => {
  const copyWidgets = copyActiveWidgetsToClipboard();
  if (copyWidgets.length === 1 && copyWidgets[0].widgetData && copyWidgets[0].widgetType && from)
    fireMPOnCopyWidget(copyWidgets[0].widgetType, from);
};

/**
 * Paste active widgets, also track the event source.
 *
 * @param from The paste event source.
 */
export const pasteActiveWidgets = (from?: 'Duplication' | 'Keyboard Paste' | 'Context Menu Paste') => {
  const widgets = pasteWidgetsToCanvas();

  if (widgets.length === 1 && widgets[0].widgetData && widgets[0].widgetType) {
    fireMPOnDuplicateWidget(widgets[0].widgetType, widgets[0].widgetData, from);
  }
};

/**
 * Copy active widgets to clipboard + local storage
 */
export const copyActiveWidgetsToClipboard = () => {
  const dispatch = store.dispatch;

  dispatch(addToPageClipboard([]));

  const storeState = store.getState() as RootState;
  const activePageId = selectActivePage(storeState);
  const activeWidgets = selectActiveWidgets(storeState);
  const widgets = selectWidgets(storeState);
  const infographId = selectInfographId(storeState);
  const widgetLayerOrder = selectPage(activePageId)(storeState).widgetLayerOrder;

  const copyWidgets = createCopyWidgets(activeWidgets, widgets, activePageId, widgetLayerOrder);
  dispatch(addToWidgetClipboard(copyWidgets));
  setLocalStorageItem(COPY_WIDGET_LOCAL_STORAGE_KEY, {
    infographId,
    copyWidgets,
  });

  return copyWidgets;
};

/**
 * Paste widgets from local storage (or clipboard if LS not defined) to canvas
 */
export const pasteWidgetsToCanvas = (from?: 'Duplication' | 'Keyboard Paste' | 'Context Menu Paste') => {
  const dispatch = store.dispatch;

  const storeState = store.getState() as RootState;
  const activePageId = selectActivePage(storeState);
  const widgetClipboard = selectWidgetClipboard(storeState);
  const copyWidgetsFromLS = getLocalStorageItem(COPY_WIDGET_LOCAL_STORAGE_KEY) as LocalStorageClipboardWidgets;
  const copyWidgets = copyWidgetsFromLS?.copyWidgets || widgetClipboard;
  const widgets = parseCopyWidgets(copyWidgets, activePageId);

  dispatch(addNewWidget(widgets));

  // Load the fonts from copied widgets
  if (copyWidgetsFromLS && copyWidgetsFromLS.copyWidgets?.length) {
    dispatch(setFontsLoaded(false));
    loadFonts(findFontsInObjectOrArray(copyWidgetsFromLS.copyWidgets)).finally(() => {
      dispatch(setFontsLoaded(true));
    });
  }

  if (copyWidgets.length === 1 && lineConnectionCache.isConnected(copyWidgets[0].copyFrom || '')) {
    Mixpanel.track(NODE_CREATED.eventName, {
      from,
    });
  }

  return widgets;
};

/**
 * Move widget by direction in widget layer order
 */
export const moveWidgetByDirection = (direction: WidgetDirection) => {
  const dispatch = store.dispatch;

  const storeState = store.getState() as RootState;
  const activePageId = selectActivePage(storeState);
  const activeWidgets = selectActiveWidgets(storeState);

  if (activeWidgets.length === 0) return;

  const moveWidgetObj = {
    pageId: activePageId,
    widgetIds: activeWidgets.map((aw) => aw.groupId || aw.responsiveGroupId || aw.id),
    direction,
  };

  dispatch(moveWidgetInLayer(moveWidgetObj));
};

/**
 * Duplicate the active widgets
 */
export const duplicateActiveWidgets = () => {
  const dispatch = store.dispatch;

  const storeState = store.getState() as RootState;
  const activePageId = selectActivePage(storeState);
  const activeWidgets = selectActiveWidgets(storeState);
  const widgets = selectWidgets(storeState);
  const widgetLayerOrder = storeState.infograph.pages[activePageId].widgetLayerOrder;

  const copyWidgets = createCopyWidgets(activeWidgets, widgets, activePageId, widgetLayerOrder);
  const newWidgets = parseCopyWidgets(copyWidgets, activePageId);

  dispatch(addNewWidget(newWidgets));

  if (newWidgets.length === 1 && newWidgets[0].widgetData && newWidgets[0].widgetType)
    fireMPOnDuplicateWidget(newWidgets[0].widgetType, newWidgets[0].widgetData);
};

/**
 * Group active widgets
 * If only one widget is active, does nothing
 */
export const groupActiveWidgets = () => {
  const dispatch = store.dispatch;

  const activeWidgets = selectActiveWidgets(store.getState() as RootState);
  if (activeWidgets?.length <= 1) return;

  dispatch(groupSelectedWidgets(activeWidgets));
};

/**
 * Ungroup active widget
 * If active widget is not a group or it is a multiselection, then does nothing
 */
export const unGroupActiveWidget = () => {
  const dispatch = store.dispatch;

  const activeWidgets = selectActiveWidgets(store.getState() as RootState);
  if (activeWidgets?.length !== 1 || !activeWidgets?.[0]?.groupId) return;

  dispatch(unGroupSelectedWidgets(activeWidgets[0].groupId));
};

/**
 * Check if the active widgets can be moved backward
 * Returns true if not the first widget in the layer order
 *
 * @param activeWidgetIds widgets to check
 * @returns
 */
export const checkIsMoveBackwardAllowed = (activeWidgetIds?: WidgetId[]) => {
  if (!activeWidgetIds?.length) return false;
  // for multiple selection it's always allowed (same as E1)
  if (activeWidgetIds.length > 1) return true;

  const storeState = store.getState() as RootState;
  const activePageId = selectActivePage(storeState);
  const widgetLayerOrder = selectPage(activePageId)(storeState).widgetLayerOrder;

  return widgetLayerOrder[0] !== activeWidgetIds[0];
};

/**
 * Check if the active widgets can be moved forward
 * Returns true if not the last widget in the layer order
 *
 * @param activeWidgetIds widgets to check
 * @returns
 */
export const checkIsMoveForwardAllowed = (activeWidgetIds?: WidgetId[]) => {
  if (!activeWidgetIds?.length) return false;
  // for multiple selection it's always allowed (same as E1)
  if (activeWidgetIds.length > 1) return true;

  const storeState = store.getState() as RootState;
  const activePageId = selectActivePage(storeState);
  const widgetLayerOrder = selectPage(activePageId)(storeState).widgetLayerOrder;

  return widgetLayerOrder[widgetLayerOrder.length - 1] !== activeWidgetIds[0];
};

/**
 * Calculates the scale/zoom factor by which the original dimensions should be multiplied with
 * in order to fit within the constraints of the thumbnail dimensions, considering
 * the optional border width.
 *
 * @function
 * @param {number} widthPx - The original width of the content in pixels.
 * @param {number} heightPx - The original height of the content in pixels.
 * @param {number} thumbnailHeight - The height of the thumbnail in pixels.
 * @param {number} thumbnailWidth - The width of the thumbnail in pixels.
 * @param {number} [borderWidth=0] - The width of the border in pixels. Default is 0.
 * @returns {number} The scale factor by which the original dimensions should be multiplied.
 *
 * @example
 * getThumbnailScale(1080, 1920, 144, 200); // Returns approximately 0.075.
 */
export const getThumbnailScale = (
  widthPx: number,
  heightPx: number,
  thumbnailHeight: number,
  thumbnailWidth: number,
  borderWidth = 0,
): number => {
  const isSmallerThanTumbnail = heightPx <= thumbnailHeight && widthPx <= thumbnailWidth;
  if (isSmallerThanTumbnail) return 1;

  const hScale = (thumbnailHeight - borderWidth) / heightPx;
  const wScale = (thumbnailWidth - borderWidth) / widthPx;

  return Math.min(wScale, hScale);
};

/**
 * Send a mixpanel event when a screenshot is captured
 * @param platform - The platform the screenshot was captured on
 * @param command - The command used to capture the screenshot
 */
export const onTrackScreenshot = ({ platform, command }: { platform: string; command: string }) => {
  const infographicId = selectInfographId(store.getState());
  Mixpanel.track(SCREENSHOT_CAPTURED.eventName, { platform, command, infographicId });
};

/**
 * Dispatches a global 'applyFont' event for the editor to listen to
 */
export const dispatchGlobalApplyFontEvent = (): void => {
  const event = new Event('applyFont');
  window.dispatchEvent(event);
};
